import 'select2';

const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');

// Selectors
const SELECTORS = {
    FORM: '#booking-form',
};

function readJsonScript(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch { return null; }
}

class ShortTermBookingApi {

    constructor() {
        this.proxyUrl = '/wp-content/themes/basellodging-theme/api-proxi.php';

        this.$app      = $('#booking-app');
        this.$loader   = $('#booking-loader');
        this.$content  = $('#booking-content');
        this.$error    = $('#booking-error');
        this.$form     = $(SELECTORS.FORM);
        this.$extras   = $('#js-extras-list');
        this.$card     = $('#js-apartment-card');
        this.$priceBox = $('#js-price-summary');
        this.$submit   = $('#js-book-submit');

        this.lang = (
            (window.ICL_LANGUAGE_CODE) ||
            (window.BL_AJAX && BL_AJAX.lang) ||
            (window.BL_I18N && BL_I18N.locale) ||
            document.documentElement.lang || 'en'
        ).toString().toLowerCase().split(/[-_]/)[0];

        this.selectedExtras = new Map();   // id -> { id, name, price, frequency }

        this.data = readJsonScript('bl-booking-data') || {};
        this.currency = this.data.currency || 'CHF';
        this.locale   = this.data.locale   || 'de-CH';

        // from payload we expect: apartmentId, propertyIdentifier, dateFrom, dateTo, guests
        this.payload = this.data.payload || {};
        this.apartmentId        = String(this.payload.apartmentId || this.payload.unitId || '');
        this.dateFrom = this.payload.dateFrom || this.payload.checkIn  || '';
        this.dateTo   = this.payload.dateTo   || this.payload.checkOut || '';
        this.guests   = Number(this.payload.guests || 1);

        this.preselectedExtraIds = new Set(
            Array.isArray(this.payload.extras) ? this.payload.extras.map(String) : []
        );

        this._hiddenExtrasSlugsSet = new Set(
            Array.isArray(this.payload.hiddenExtras)
                ? this.payload.hiddenExtras.map(s => this.toFeatureId(s))
                : []
        );

        // data from API
        this.availability = null;   // full response (unit, rateAvailability, etc.)
        this.apartment    = null;   // info for the card (title, location, photos, ... )
        this.apartmentTitle    = null;
        this.extras       = [];     // array of extra services

        // initialize select2 + validation right away (form is hidden but ready)
        this.initSelects(this.$form);
        this.validateForm();

        this.stripe     = null;
        this.elements   = null;
        this.cardNumber = null;
        this.cardExpiry = null;
        this.cardCvc    = null;

        this.submitAttempted = false;

        this.lastTotal  = 0;

        this.lastTotal  = 0;

        // Extras assets (array of { slug, icon, logo })
        this.extrasAssets = (window.BL_AJAX && (
            BL_AJAX.extras_icons_assets ||
            BL_AJAX.extras_icons_items  ||
            BL_AJAX.extras_assets
        )) || [];

        // Backward-compat: old map (slug -> iconUrl) if exists
        this.extrasIconMap = (window.BL_AJAX && BL_AJAX.extras_icons_map) ? BL_AJAX.extras_icons_map : {};

        // Normalize assets to array
        if (!Array.isArray(this.extrasAssets)) this.extrasAssets = [];

        // Fast lookups
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);
        this._reindexExtrasAssets();

        this._missingExtrasSlugs = new Set();
        this._extrasIconsSyncing = false;

        this.checkoutToken = this.data?.token || window.BL_CHECKOUT_TOKEN || '';
    }


    _buildExtrasOrderIndex() {
        // Order comes from BL_AJAX.extras_assets array (slug order)
        const order = Object.create(null);

        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        let pos = 0;

        for (let i = 0; i < arr.length; i++) {
            const slug = String(arr[i]?.slug || '').trim();
            if (!slug) continue;
            if (slug === 'default') continue;

            // keep first occurrence only
            if (order[slug] == null) {
                order[slug] = pos++;
            }
        }

        return order;
    }

    _getExtrasSortedByAssetsOrder(extras) {
        const list = Array.isArray(extras) ? extras : [];
        if (!list.length) return [];

        const order = this._buildExtrasOrderIndex();

        // stable sort: preserve API order inside same bucket
        return list
            .map((svc, originalIndex) => {
                const slug = this.getExtraSlugFromService(svc);
                const ord = (slug && order[slug] != null) ? order[slug] : Number.MAX_SAFE_INTEGER;
                return { svc, ord, originalIndex };
            })
            .sort((a, b) => (a.ord - b.ord) || (a.originalIndex - b.originalIndex))
            .map(x => x.svc);
    }

    _reindexExtrasAssets() {
        // English comments as requested
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);

        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        for (let i = 0; i < arr.length; i++) {
            const it = arr[i] || {};
            const slug = String(it.slug || '').trim();
            if (!slug) continue;

            const icon = String(it.icon || '').trim();
            const logo = String(it.logo || '').trim();

            if (icon) this._extrasIconBySlug[slug] = icon;
            if (logo) this._extrasLogoBySlug[slug] = logo;
        }
    }

    _mergeExtrasAssets(incoming) {
        // Accept array of items OR object map, be tolerant
        let items = [];

        if (Array.isArray(incoming)) {
            items = incoming;
        } else if (incoming && typeof incoming === 'object') {
            items = Object.keys(incoming).map((slug) => {
                const v = incoming[slug];
                if (v && typeof v === 'object') return { slug, icon: v.icon || '', logo: v.logo || '' };
                return { slug, icon: v || '', logo: '' };
            });
        }

        if (!items.length) return;

        // Merge into this.extrasAssets by slug
        const bySlug = Object.create(null);
        (this.extrasAssets || []).forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;
            bySlug[s] = { ...it };
        });

        items.forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;

            const prev = bySlug[s] || { slug: s };
            bySlug[s] = {
                slug: s,
                icon: (it.icon != null && String(it.icon).trim() !== '') ? it.icon : (prev.icon || ''),
                logo: (it.logo != null && String(it.logo).trim() !== '') ? it.logo : (prev.logo || ''),
            };
        });

        this.extrasAssets = Object.values(bySlug);
        this._reindexExtrasAssets();
    }

    /* ---------- utils ---------- */
    currencyFormat(v) {
        const n = Number(v); if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
        } catch { return `${this.currency} ${n.toFixed(0)}`; }
    }
    currencyFormatTwoDecimals(v) {
        const n = Number(v); if (!Number.isFinite(n)) return '';
        try {
            return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
        } catch { return `${this.currency} ${n.toFixed(0)}`; }
    }

    escapeHtml(s){return String(s).replace(/[&<>"']/g,m=>({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[m]));}

    nights(df,dt){
        if(!df||!dt)return 0;
        const a=new Date(df+'T00:00:00Z'), b=new Date(dt+'T00:00:00Z');
        return Math.max(0, Math.round((b-a)/86400000));
    }

    async loadPhonesAjax() {
        const [list, preferred] = await Promise.all([
            $.getJSON('/wp-json/basel/v1/phones'),
            $.getJSON('/wp-json/basel/v1/preferred'),
        ]);
        this._phonesList = Array.isArray(list) ? list : [];
        this._preferredCodes = Array.isArray(preferred) ? preferred : [];
    }

    populateDialCodesFromData() {
        const list = this._phonesList || [];
        const prefSet = new Set((this._preferredCodes || []).map(c => String(c).toUpperCase()));
        const byCode = new Map(list.map(x => [String(x.code||'').toUpperCase(), x]));

        const first = [];
        (this._preferredCodes || []).forEach(code => {
            const up = String(code).toUpperCase();
            if (byCode.has(up)) first.push(byCode.get(up));
        });
        const rest = list.filter(x => !prefSet.has(String(x.code||'').toUpperCase()));
        const finalList = first.concat(rest);

        const html = finalList.map(x => {
            const dial = String(x.dial_code||'').trim();
            const iso  = String(x.code||'').toUpperCase();
            return `<option value="${this.escapeHtml(dial)}">${this.escapeHtml(`${dial} ${iso}`)}</option>`;
        }).join('');

        this.$form.find('select[name="phone-code"], select[name="additional-phone-code"]').html(html);

        this.$form.find('select[name^="guests["][name$="[phoneCode]"]').html(html);
        this.initSelects(this.$form);

        this.initSelects(this.$form);
    }

    populateCountriesFromData() {
        const list = this._phonesList || [];
        const prefSet = new Set((this._preferredCodes || []).map(c => String(c).toUpperCase()));
        const byCode = new Map(list.map(x => [String(x.code||'').toUpperCase(), x]));

        const first = [];
        (this._preferredCodes || []).forEach(code => {
            const up = String(code).toUpperCase();
            if (byCode.has(up)) first.push(byCode.get(up));
        });
        const rest = list.filter(x => !prefSet.has(String(x.code||'').toUpperCase()));
        const finalList = first.concat(rest);

        const html = ['<option value="">'+t('select_country', 'Select country')+'</option>']
            .concat(finalList.map(x => `<option value="${this.escapeHtml(String(x.code||'').toLowerCase())}">${this.escapeHtml(String(x.name||''))}</option>`))
            .join('');

        this.$form.find('select[name="country"], select[name="additional-country"]').html(html);
        this.initSelects(this.$form);
    }


    toFeatureId(nameOrId = '') {
        return String(nameOrId)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_')      // spaces -> underscores
            .replace(/_+/g, '_')       // collapse multiple _
            .replace(/^_+|_+$/g, '');  // trim _
    }

    getExtraSlugFromService(svc) {
        // Prefer stable API-provided slug if exists
        if (svc && svc.slug) return this.toFeatureId(svc.slug);

        // Use original (non-localized) name to stay consistent across languages
        const name = String((svc && (svc.name || svc.webName || svc.title)) || '').trim();
        return this.toFeatureId(name);
    }

    getExtraIconUrlBySlug(slug) {
        const s = String(slug || '').trim();

        // Prefer assets first
        if (s && this._extrasIconBySlug && this._extrasIconBySlug[s]) return this._extrasIconBySlug[s];

        // Backward-compat: old map
        const map = this.extrasIconMap || {};
        if (s && Object.prototype.hasOwnProperty.call(map, s) && map[s]) return map[s];

        // fallback to default
        if (this._extrasIconBySlug && this._extrasIconBySlug['default']) return this._extrasIconBySlug['default'];
        if (Object.prototype.hasOwnProperty.call(map, 'default') && map['default']) return map['default'];

        return '';
    }

    getExtraLogoUrlBySlug(slug) {
        const s = String(slug || '').trim();
        if (s && this._extrasLogoBySlug && this._extrasLogoBySlug[s]) return this._extrasLogoBySlug[s];

        // optional fallback
        if (this._extrasLogoBySlug && this._extrasLogoBySlug['default']) return this._extrasLogoBySlug['default'];
        return '';
    }

    queueMissingExtraSlug(slug) {
        const s = String(slug || '').trim();
        if (!s) return;

        // If we already have icon or logo for this slug — nothing to sync
        const hasIcon = !!(this._extrasIconBySlug && this._extrasIconBySlug[s]);
        const hasLogo = !!(this._extrasLogoBySlug && this._extrasLogoBySlug[s]);

        // Also treat legacy icon map as "has icon"
        const legacyHasIcon = !!(this.extrasIconMap && this.extrasIconMap[s]);

        if (hasIcon || hasLogo || legacyHasIcon) return;

        this._missingExtrasSlugs.add(s);
    }

    flushMissingExtrasIcons() {
        if (this._extrasIconsSyncing) return;

        const slugs = Array.from(this._missingExtrasSlugs);
        if (!slugs.length) return;

        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && (BL_AJAX.extras_icons_nonce || BL_AJAX.nonce)) || '';

        this._missingExtrasSlugs.clear();
        this._extrasIconsSyncing = true;

        $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_sync_extras_icons',
                _ajax_nonce: nonce,
                slugs: slugs
            }
        })
            .done((res) => {
                // Expect: res.data.items = [{slug,icon,logo}, ...]
                const payload  = res?.data || {};
                const incoming = payload.items || payload.assets || payload.map || payload;

                this._mergeExtrasAssets(incoming);

                // Optional: keep legacy map in sync (icon only), so old code won't break
                if (Array.isArray(this.extrasAssets)) {
                    this.extrasIconMap = this.extrasIconMap || {};
                    this.extrasAssets.forEach((it) => {
                        const s = String(it?.slug || '').trim();
                        if (!s) return;
                        if (it.icon) this.extrasIconMap[s] = it.icon;
                    });
                }
            })
            .always(() => {
                this._extrasIconsSyncing = false;
            });
    }

    getPaymentMethod() {
        return String($("input[name='payment']:checked").val() || 'credit-card');
    }

    computeExtraCost(ex){               // price of an extra service (0 if missing)
        const p = Number(ex?.price || 0);
        return Number.isFinite(p) ? p : 0;
    }

    formatFrequency(freq){
        if(!freq) return 'One-time fee';
        const f = String(freq).toLowerCase();
        if (f==='one_time' || f==='one-time') return 'One-time fee';
        if (f==='per_night') return 'Per night';
        if (f==='per_stay')  return 'Per stay';
        return f;
    }

    getExtrasTotal(){
        let t = 0;
        this.selectedExtras.forEach(ex => t += this.computeExtraCost(ex));
        return t;
    }

    getSelectedRateId() {
        const ra = this.apartment?.rateAvailabilities?.[0]
            || this.apartment?.rateAvailabilities
            || null;
        return Number(ra?.rateId || 0);
    }

    async ensureCheckoutTokenValid() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        if (!this.checkoutToken) {
            const msg = t('booking_session_is_missing', 'Your booking session is missing. Please start again.');
            throw new Error(msg);
        }

        const res = await $.ajax({
            url: ajaxUrl, method: 'POST', dataType: 'json',
            data: { action: 'bl_validate_checkout_token', _ajax_nonce: nonce, token: this.checkoutToken }
        });

        if (!res?.success) {
            throw new Error(res?.data?.message || t('booking_session_expired', 'Your booking session expired. Please start again.'));
        }
        return true;
    }

    // ---- helpers (frontend) ---------------------------------

    // ↓ ДОДАТИ всередині класу ShortTermBookingApi
    getStripeBillingDetails() {
        const useSame = $('#sameBilling').is(':checked');

        const readPersonal = () => {
            const code = String(this.$form.find('select[name="phone-code"]').val() || '').trim();
            const num  = String(this.$form.find('input[name="phoneNumber"]').val() || '').trim();
            const first= String(this.$form.find('input[name="firstName"]').val() || '').trim();
            const last = String(this.$form.find('input[name="lastName"]').val()  || '').trim();
            const email= String(this.$form.find('input[name="email"]').val()      || '').trim();
            const addr = String(this.$form.find('input[name="address"]').val()    || '').trim();
            const city = String(this.$form.find('input[name="city"]').val()       || '').trim();
            const zip  = String(this.$form.find('input[name="zipCode"]').val()    || '').trim();
            const country = String(this.$form.find('select[name="country"]').val() || '').trim().toUpperCase();

            return {
                fullName: [first, last].filter(Boolean).join(' '),
                email, phone: [code, num].filter(Boolean).join(' '),
                address: { line1: addr, city, postal_code: zip, country }
            };
        };

        const readBilling = () => {
            const code = String(this.$form.find('select[name="additional-phone-code"]').val() || '').trim();
            const num  = String(this.$form.find('input[name="billingPhoneNumber"]').val() || '').trim();
            const first= String(this.$form.find('input[name="billingFirstName"]').val() || '').trim();
            const last = String(this.$form.find('input[name="billingLastName"]').val()  || '').trim();
            const email= String(this.$form.find('input[name="billingEmail"]').val()      || '').trim();
            const addr = String(this.$form.find('input[name="billingAddress"]').val()    || '').trim();
            const city = String(this.$form.find('input[name="billingCity"]').val()       || '').trim();
            const zip  = String(this.$form.find('input[name="billingZipCode"]').val()    || '').trim();
            const country = String(this.$form.find('select[name="additional-country"]').val() || '').trim().toUpperCase();

            return {
                fullName: [first, last].filter(Boolean).join(' '),
                email, phone: [code, num].filter(Boolean).join(' '),
                address: { line1: addr, city, postal_code: zip, country }
            };
        };

        // cardholder name (опціонально) — пріоритетніше за ім’я з форм
        const cardholder = String(this.$form.find('input[name="cardholder_name"]').val() || '').trim();

        const src = useSame ? readPersonal() : readBilling();

        return {
            name: (cardholder || src.fullName) || undefined,
            email: src.email || undefined,
            phone: src.phone || undefined,
            address: src.address
        };
    }

    parseAjaxError(err) {
        try {
            // jQuery xhr
            if (err && err.responseJSON) {
                const rj = err.responseJSON;
                // WP: { success:false, data:{message:"..."} }
                if (rj.data && rj.data.message) return rj.data.message;
                if (rj.message) return rj.message;
            }
            if (err && err.responseText) {
                // інколи приходить plain text або JSON строкою
                try {
                    const j = JSON.parse(err.responseText);
                    if (j?.data?.message) return j.data.message;
                    if (j?.message) return j.message;
                } catch { /* ignore */ }
                return String(err.responseText).trim();
            }
            // звичайний Error
            if (err && err.message) return err.message;
        } catch {}
        return t('unexpected_error', 'Unexpected error. Please try again.');
    }

    showStripeError(msg) {
        $('#bl-card-errors').text(msg || '').toggleClass('hidden', !msg);
    }

    showGlobalError(msg) {
        const $box = $('#js-form-global-error');
        if ($box.length) {
            $box.text(msg || '').toggleClass('hidden', !msg);
        } else {
            alert(msg || t('something_went_wrong', 'Something went wrong.'));
        }
    }

    showFormError(msg) {
        const $box = $('#js-form-global-error');
        if ($box.length) $box.text(msg || t('something_went_wrong', 'Something went wrong.')).removeClass('hidden');
        else alert(msg || t('something_went_wrong', 'Something went wrong.'));
    }

    clearFormError() {
        const $box = $('#js-form-global-error');
        if ($box.length) $box.addClass('hidden').text('');
    }

    /* ---------- UI: loader / content / error ---------- */
    showLoader(){ this.$loader.removeClass('hidden'); this.$content.addClass('hidden'); this.$error.addClass('hidden'); }
    showContent(){ this.$loader.addClass('hidden'); this.$error.addClass('hidden'); this.$content.removeClass('hidden'); }
    showError(msg){
        this.$loader.addClass('hidden');
        this.$error.text(msg || t('apartment_is_not_available', 'The apartment is not available on these dates.')).removeClass('hidden');
        this.$content.addClass('hidden');
    }

    /* ---------- AJAX ---------- */

    // 1) Availability check (Promise)
    checkAvailability() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        const unitTypeType = (this.data?.payload?.unitTypeType || '').toString();
        const maxGuests = (this.data?.payload?.maxGuests || '').toString();

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_check_availability',
                _ajax_nonce: nonce,
                lang: this.lang,
                dateFrom: this.dateFrom,
                dateTo:   this.dateTo,
                guests:   String(this.guests),
                apartmentId: this.apartmentId,
                maxGuests, unitTypeType
            }
        }).then((res) => {
            if (!res?.success || (res?.data?.available === false)) {
                const msg = res?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                return Promise.reject(new Error(msg));
            }
            // store payload in the class
            this.availability = res.data || {};
            // put unit in a convenient place
            this.apartment = this.availability.unit || this.availability;
            this.apartmentTitle = this.availability.unitTitle || 'Apartment';
            return this.availability;
        });
    }

    // 2) Detailed info for card/extras (Promise)
    fetchApartmentInformation() {
        if (!this.apartmentId) return Promise.resolve({});
        return $.ajax({
            url: `${this.proxyUrl}?type=getApartmentInformation`,
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify({ apartment_id: this.apartmentId })
        }).then((resp) => {
            const data = (resp && typeof resp === 'object' && 'data' in resp) ? resp.data : resp;
            this.apartmentInfo = data || {};
            this.extras = Array.isArray(data?.extras) ? data.extras : [];
            return data;
        });
    }

    /* ---------- RENDER ---------- */

    renderApartmentCard() {
        const title    = this.apartmentTitle;
        const location = this.apartmentInfo?.location || this.apartment?.location || '';
        const guests   = Math.max(1, Number(this.guests) || 1);
        const br       = (this.apartmentInfo?.specifications?.numberOfBedrooms ?? 0);
        const brLabel  = (Number(br) === 0 ? t('studio', 'Studio') : `${br} BR`);
        const bath     = (this.apartmentInfo?.bathrooms ?? this.apartment?.bathrooms ?? 1);
        const areaMin  = this.apartmentInfo?.specifications?.area?.min || '';
        const areaMax  = this.apartmentInfo?.specifications?.area?.max || '';
        const mainImg  = this.apartmentInfo?.mainImage?.url || this.apartmentInfo?.images?.[0] || '';
        const area     = areaMin && areaMax && areaMin !== areaMax ? `${areaMin}–${areaMax}m²` : (areaMin || areaMax ? `${areaMin || areaMax}m²` : '');

        const base = '/wp-content/themes/basellodging-theme/imgs';
        const placeholder = base ? base + "/placeholder.svg" : "/placeholder.svg";

        const n = this.nights(this.dateFrom, this.dateTo);

        this.$card.html(`
      <div class="rounded-lg bg-card text-card-foreground shadow-lg border border-gray-200 overflow-hidden">
        <div class="relative">
          <img src="${mainImg? mainImg : placeholder}"
               alt="${this.escapeHtml(title)}" class="w-full h-48 object-cover">
        </div>
        <div class="p-4">
          ${location ? `
            <div class="mb-2">
              <div class="flex items-center text-sm text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
                ${this.escapeHtml(location)}
              </div>
            </div>
          ` : ``}
          <h3 class="font-semibold text-basel-gray mb-3">${this.escapeHtml(title)}</h3>

          <div class="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <span>${guests} ${guests===1?t('guest','Guest').toLowerCase():t('guests','Guests').toLowerCase()}</span>
            </div>
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></svg>
              <span>${this.escapeHtml(brLabel)}</span>
            </div>
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M10 4 8 6"></path><path d="M17 19v2"></path><path d="M2 12h20"></path><path d="M7 19v2"></path><path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"></path></svg>
              <span>${bath} ${bath===1?t('bath','bath').toLowerCase():t('baths','baths').toLowerCase()}</span>
            </div>
            ${area ? `
            <div class="flex items-center gap-1 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="3" rx="2"></rect></svg>
              <span>${this.escapeHtml(area)}</span>
            </div>` : ``}
          </div>

          <div class="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
            <div class="flex items-center gap-2 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>
              <div><div class="text-xs text-gray-500">${t('arrival','Arrival Date')}</div><div class="font-medium">${this.escapeHtml(this.dateFrom)}</div></div>
            </div>
            <div class="h-px bg-gray-200 my-2"></div>
            <div class="flex items-center gap-2 text-sm text-basel-gray">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-basel-blue" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>
              <div><div class="text-xs text-gray-500">${t('departure','Departure Date')}</div><div class="font-medium">${this.escapeHtml(this.dateTo)}</div></div>
            </div>
            <div class="mt-2 pt-2 border-t border-gray-100">
              <div class="text-xs text-gray-500">${n} ${n===1?t('night','night').toLowerCase():t('nights','nights').toLowerCase()} ${t('total','total').toLowerCase()}</div>
            </div>
          </div>
        </div>
      </div>
    `);
    }


    pruneHiddenSelectedExtras() {
        const hidden = this._hiddenExtrasSlugsSet;
        if (!hidden || !hidden.size) return;

        // Build the same id index as in hydrateSelectedExtrasFromPayload()
        const byId = new Map((this.extras || []).map(s => [
            String(s.externalId ?? s.id ?? s.extraId ?? ''),
            s
        ]));

        const idsToDelete = [];

        this.selectedExtras.forEach((_ex, id) => {
            const svc = byId.get(String(id));
            if (!svc) return;

            const slug = this.getExtraSlugFromService(svc);
            if (slug && hidden.has(slug)) idsToDelete.push(String(id));
        });

        idsToDelete.forEach(id => this.selectedExtras.delete(id));
    }

    renderExtras() {
        if (!this.extras.length) {
            this.$extras.html('<div class="text-sm text-gray-500">' + t('no_extra_services', 'No extra services available.') + '</div>');
            return;
        }

        const freqLabel = (f) => {
            const v = String(f || '').toLowerCase();
            if (v === 'monthly') return t('freq_monthly','monthly');
            if (v === 'weekly')  return t('freq_weekly','weekly');
            if (v === 'daily')   return t('freq_daily','daily');
            return t('freq_one_off','one-off');
        };

        const extrasSorted = this._getExtrasSortedByAssetsOrder(this.extras);

        const rows = extrasSorted.map((s) => {
            if (s.webCategoryName !== 'short') return '';

            const id = String(s.id ?? '');
            const name = IS_DE ? (s.descriptionLang2 || "") : (s.name || "");
            const desc = (() => {
                const src = String(s.webDescription || '').trim();
                if (!src) return '';
                const parts = src.split('/').map(p => p.trim()).filter(Boolean);
                if (!parts.length) return '';
                return IS_DE ? parts[parts.length - 1] : parts[0];
            })();

            const freq = s.billing_frequency ? String(s.billing_frequency).trim() : '';
            const freqLower = freq.toLowerCase();

            const price = Number(s.price || 0);
            const priceStr = this.currencyFormatTwoDecimals(price);

            const isOn = this.selectedExtras.has(id);

            const slug = this.getExtraSlugFromService(s);

            if (this._hiddenExtrasSlugsSet && this._hiddenExtrasSlugsSet.has(slug)) return '';

            // ensure default exists
            this.queueMissingExtraSlug('default');

            // if slug is new — queue it
            this.queueMissingExtraSlug(slug);

            const iconUrl = this.getExtraIconUrlBySlug(slug);
            const logoUrl = this.getExtraLogoUrlBySlug(slug);

            const iconHtml = iconUrl
                ? `<img class="w-8 h-8 object-contain" src="${this.escapeHtml(iconUrl)}" alt="" loading="lazy" />`
                : '';

            const partnerHtml = logoUrl ? `
            <div class="sm:flex shrink-0 items-center justify-center logo-box">
              <div class="flex flex-col items-center sm:items-start gap-2">
                <img class="w-24 h-24 object-contain" src="${this.escapeHtml(logoUrl)}" alt="" loading="lazy" />
              </div>
            </div>
        ` : '';

            return `
            <div class="bg-white p-4 border border-blue-100 rounded-lg transition-colors ${isOn ? 'border-green-500 bg-green-50' : ''}"
                 data-extra data-id="${this.escapeHtml(id)}" data-name="${this.escapeHtml(name)}"
                 data-frequency="${this.escapeHtml(freqLower)}">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-0 extras-box">

                    <!-- Col 1: Icon -->
                    <div class="flex items-center justify-center gap-3 shrink-0 icon-box">
                        ${iconHtml ? `<span class="inline-flex items-center justify-center">${iconHtml}</span>` : ''}
                    </div>

                    <!-- Col 2: Info -->
                    <div class="info-box">
                        <div class="flex flex-wrap items-center gap-2">
                            <h3 class="font-medium text-basel-gray text-sm sm:text-base break-words">
                                ${this.escapeHtml(name)}
                            </h3>
                            ${freqLower ? `
                                <span class="text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded-full px-2 py-0.5">
                                  ${this.escapeHtml(freqLabel(freqLower))}
                                </span>
                            ` : ''}
                        </div>

                        ${desc ? `<p class="mt-1 text-xs sm:text-sm text-gray-600">${this.escapeHtml(desc)}</p>` : ''}
                    </div>

                    <!-- Price -->
                    <div class="flex items-baseline gap-2 price-box">
                        ${price ? `
                            <div class="font-bold text-basel-gray text-sm sm:text-base">
                              ${this.escapeHtml(priceStr)}
                            </div>
                        ` : ''}
                    </div>

                    <!-- Col 3: Partner Logo -->
                    ${partnerHtml}

                    <!-- Col 4: CTA (keep booking button class) -->
                    <div class="flex sm:ml-auto shrink-0 justify-end cta-box">
                        <button type="button"
                                class="inline-flex items-center justify-center gap-2 text-sm h-9 rounded-md px-3 js-extra-book
                                       ${isOn ? 'bg-green-600 hover:bg-green-700 text-white' : 'border bg-background border-gray-300 text-basel-gray hover:bg-gray-50'}"
                                data-id="${this.escapeHtml(id)}"
                                data-name="${this.escapeHtml(name)}"
                                data-price="${price}"
                                data-frequency="${this.escapeHtml(freqLower)}">
                            ${isOn ? t('added', 'Added') : t('add', 'Add')}
                        </button>
                    </div>

                </div>
            </div>
        `;
        }).join('');

        this.$extras.html(rows);

        // important: try to sync missing icons/logos
        this.flushMissingExtrasIcons();
    }

    hydrateSelectedExtrasFromPayload() {
        if (!this.preselectedExtraIds || !this.preselectedExtraIds.size) return;

        const byId = new Map((this.extras || []).map(s => [
            String(s.externalId ?? s.id ?? s.extraId ?? ''),
            s
        ]));

        this.selectedExtras.clear();

        this.preselectedExtraIds.forEach((rawId) => {
            const id = String(rawId);
            if (!byId.has(id)) return;

            const s = byId.get(id);
            this.selectedExtras.set(id, {
                id,
                name: String(s.extraName ?? s.name ?? ''),
                price: Number(s.netPrice ?? s.price ?? 0),
                frequency: String(s.billing_frequency ?? s.frequency ?? '').toLowerCase()
            });
        });
    }

    // returns total (number)
    renderPriceBox() {
        const ra = this.apartment?.rateAvailabilities?.[0]
            || this.apartment?.rateAvailabilities
            || null;

        if(!ra) return 0;

        const nightsApi  = Number(ra?.nights || 0);
        const n          = nightsApi > 0 ? nightsApi : this.nights(this.dateFrom, this.dateTo);
        const priceNight = Number(ra?.avgTotals?.net ?? ra?.priceFrom?.gross) || 0;
        const line       = priceNight * n;

        const netFromApi = Number(ra?.totals?.net || 0);
        const vatAmount  = Number(ra?.totals?.vat || 0);
        const grossFromApi = Number(ra?.totals?.gross || 0);

        const vatPercentNum = netFromApi > 0 ? (vatAmount / netFromApi) * 100 : 0;
        const vatPercentStr = (Math.round(vatPercentNum * 10) / 10).toFixed(1);

        const guestsCount = Number(this.guests) || 1;   // к-сть гостей
        const GUEST_TAX_CHF = 4.20;                     // ставка за особу/ніч
        const guestTax = Math.max(0, GUEST_TAX_CHF * guestsCount * n);

        let cleaning = Number(this.apartmentInfo?.pricing?.cleaningFee || 0);

        const extrasTotal = this.getExtrasTotal();
        const extrasLines = Array.from(this.selectedExtras.values()).map(x => `
          <div class="flex justify-between text-sm text-basel-gray">
            <div>
              <div>${this.escapeHtml(x.name)}</div>
            </div>
            <span>${this.currencyFormat(this.computeExtraCost(x))}</span>
          </div>
        `).join('');

        const subtotalNet = netFromApi || (line + cleaning);
        const vatToShow   = vatAmount || 0;
        const totalBase   = grossFromApi || (subtotalNet + vatToShow);
        const total       = totalBase + extrasTotal + guestTax;

        const box = `
          <div class="rounded-lg bg-card text-card-foreground shadow-lg border border-gray-200">
            <div class="flex flex-col space-y-1.5 p-6 border-b border-gray-100">
              <h3 class="tracking-tight text-lg font-semibold text-basel-gray">${t('price_summary', 'Price Summary')}</h3>
            </div>
            <div class="p-6 pt-6 space-y-4">
              <div class="space-y-3">
                <div class="flex justify-between text-sm text-basel-gray">
                  <span>${n} ${n===1?t('night','night').toLowerCase():t('nights','nights').toLowerCase()} × ${this.currencyFormat(priceNight)}</span>
                  <span>${this.currencyFormat(line)}</span>
                </div>
                ${vatToShow > 0 ? `
                <div class="flex justify-between text-sm text-basel-gray">
                  <span>${t('vat','VAT')} (${vatPercentStr}%)</span><span>${this.currencyFormat(vatToShow)}</span>
                </div>` : ``}
                ${guestTax > 0 ? `
                <div class="flex justify-between text-sm text-basel-gray">
                  <span>${t('guest_tax','Guest Tax (%s per person)').replace('%s', this.currencyFormat(GUEST_TAX_CHF))}</span>
                  <span>${this.currencyFormat(guestTax)}</span>
                </div>` : ``}
                ${extrasLines}
              </div>
              <div class="h-px bg-gray-200 my-4"></div>
              <div class="flex justify-between font-bold text-xl text-basel-gray">
                <span>${t('total','Total')}</span><span id="js-total-amount">${this.currencyFormat(total)}</span>
              </div>
            </div>
          </div>
        `;

        this.$priceBox.html(box);

        this.lastTotal = Number(total) || 0;

        return total;
    }

    setPayButtonAmount(total) {
        if (!this.$submit.length) return;
        this.$submit.text(`${t('book_and_pay', 'Book & Pay Now')} (${this.currencyFormat(total)})`);
    }

    // AJAX: fetch HTML form and inject into the placeholder
    loadFormHtml() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_get_booking_form',
                _ajax_nonce: nonce,
                guests: String(this.guests || 1)
            }
        }).then((res) => {
            if (!res?.success || !res?.data?.html) {
                return Promise.reject(new Error(t('could_not_load_the_booking_form','Could not load the booking form.')));
            }

            const $wrap = $('[data-role="form"]');      // placeholder for the form
            $wrap.html(res.data.html);                  // insert the form

            // after insertion re-initialize element references
            this.$form   = $(SELECTORS.FORM);
            this.$extras = $('#js-extras-list');
            this.$submit = $('#js-book-submit');

            // initialize select2/validation on the dynamically loaded form
            this.initSelects(this.$form);
            this.validateForm();
        });
    }

    /* ---------- form/init ---------- */

    initSelects($scope) {
        const initOne = ($el, placeholder) => {
            $el.each(function () {
                const $s = $(this);

                if ($s.hasClass('select2-hidden-accessible')) {
                    $s.select2('destroy');
                }

                $s.select2({
                    minimumResultsForSearch: 0,
                    width: '100%',
                    dropdownParent: $s.closest('.select-box'),
                    dropdownAutoWidth: true,
                    placeholder,
                    theme: 'basel'
                });
            });
        };

        const placeholderCountry = t('select_country', 'Select country');

        [
            { el: $scope.find('select[name="country"]'),               placeholder: placeholderCountry },
            { el: $scope.find('select[name="additional-country"]'),    placeholder: placeholderCountry },
            { el: $scope.find('select[name="phone-code"]'),            placeholder: '' },
            { el: $scope.find('select[name="additional-phone-code"]'), placeholder: '' },
            // усі phoneCode у блоках гостей
            { el: $scope.find('select[name^="guests["][name$="[phoneCode]"]'), placeholder: '' }
        ].forEach(({ el, placeholder }) => el.length && initOne(el, placeholder));
    }

    setupFormValidation(form) {
        let self = this;

        $.extend($.validator.messages, {
            required: t('val_required', 'This field is required.'),
            email:    t('val_email', 'Please enter a valid email address.'),
            number:   t('val_number', 'Please enter a valid number.'),
            digits:   t('val_digits', 'Please enter only digits.'),
            minlength: $.validator.format(t('val_minlength', 'Please enter at least {0} characters.')),
            maxlength: $.validator.format(t('val_maxlength', 'Please enter no more than {0} characters.')),
            equalTo:  t('val_equalTo', 'Please enter the same value again.')
        });

        $.validator.addMethod('blPhoneLocal', function (value, element, params) {
            if (this.optional(element)) return true;
            const digits = String(value || '').replace(/\D/g, '');
            const min = (params && params.min) || 6;
            const max = (params && params.max) || 12;
            return digits.length >= min && digits.length <= max;
        }, t('val_phone', 'Please enter a valid phone number.'));

        $.validator.addMethod('blPostalUniversal', function (value, element) {
            if (this.optional(element)) return true;

            const v = String(value || '').trim();

            if (!/^[A-Za-z0-9][A-Za-z0-9 \-]*$/.test(v)) return false;

            const alnumLen = v.replace(/[^A-Za-z0-9]/g, '').length;

            return alnumLen >= 2 && alnumLen <= 12;
        }, t('val_postal', 'Please enter a valid postal/ZIP code.'));

        $.validator.addMethod('blEmail', function (value, element) {
            if (this.optional(element)) return true;

            const v = String(value || '').trim();
            if (!v || v.length > 254) return false;

            // local-part ≤ 64 (як рекомендація)
            const at = v.indexOf('@');
            if (at <= 0 || at > 64) return false;

            // Базовий RFC5322-lite + TLD 2+
            const re = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            if (!re.test(v)) return false;

            // Забороняємо подвійні крапки у домені
            const domain = v.slice(at + 1);
            if (domain.includes('..')) return false;

            return true;
        }, t('val_email', 'Please enter a valid email address.'));



        $(SELECTORS.FORM).validate({
            focusInvalid: false,

            invalidHandler: function (_event, validator) {
                const first = validator.errorList?.[0]?.element;
                if (first) {
                    const $first = $(first);
                    const $wrap = $first.parent();
                    self.scrollToElem($wrap);
                    setTimeout(() => $first.trigger('focus'), 0);
                }
                $(this).find('button[type="submit"]').prop('disabled', false).removeClass('loading');
            },

            submitHandler: async (form, event) => {
                event.preventDefault();
                this.submitAttempted = true;

                this.showStripeError('');
                this.showGlobalError('');

                const $form = $(form);
                const $btn  = $form.find('button[type="submit"]');
                $btn.prop('disabled', true).addClass('loading');

                try {
                    await this.ensureCheckoutTokenValid();

                    const plainValid = this.validateInputs();
                    if (!plainValid) return;

                    const pm = this.getPaymentMethod();

                    // 2) wire-transfer — повністю пропускаємо Stripe
                    if (pm === 'wire-transfer') {
                        await this.finalizeBooking(null, 'wire-transfer');
                        return;
                    }

                    // 3) card — перевірка Stripe елементів
                    const stripeOk = this.validateStripeElements(true);
                    if (!stripeOk) return;

                    // 4) Stripe flow
                    await this.onSubmitStripeFlow();
                } catch (err) {
                    this.showStripeError('');
                    this.showGlobalError(this.parseAjaxError(err));
                } finally {
                    $btn.prop('disabled', false).removeClass('loading');
                }
            },


            rules: {
                phoneNumber: {
                    blPhoneLocal: {
                        param: { min: 6, max: 12 }
                    }
                },
                billingPhoneNumber: {
                    blPhoneLocal: {
                        param: { min: 6, max: 12 },
                        depends: function(){ return !$('#sameBilling').is(':checked'); }
                    }
                },
                zipCode: {
                    blPostalUniversal: true
                },
                billingZipCode: {
                    blPostalUniversal: {
                        depends: function(){ return !$('#sameBilling').is(':checked'); }
                    }
                },
                email: {
                    blEmail: true
                },
                billingEmail: {
                    blEmail: {
                        depends: function(){ return !$('#sameBilling').is(':checked'); }
                    }
                }
            },

            errorPlacement: function (error, element) {
                if(element.is('[type="checkbox"]')) return;
                error.appendTo(element.parent());
            },

            highlight: function (element) {
                $(element).parent().addClass('error');
            },

            unhighlight: function (element) {
                $(element).parent().removeClass('error');
            }
        });
    }

    showHiddenFields() {
        $('[data-show-triggers]').each((index, element) => {
            const $container = $(element);
            const triggers = JSON.parse($container.attr('data-show-triggers').replace(/'/g, '"'));
            this.checkTriggers($container, triggers);
        });
    }

    hideShownFields() {
        $('[data-hide-triggers]').each((index, element) => {
            const $container = $(element);
            const triggers = JSON.parse($container.attr('data-hide-triggers').replace(/'/g, '"'));
            this.uncheckTriggers($container, triggers);
        });
    }

    // Restore required on previously required fields inside the container
    restoreRequiredInside($container) {
        // Only inputs that were marked before hiding should get required back
        $container.find('.required-if-showed').each(function () {
            const $f = $(this);
            $f.addClass('was-required'); // optional trace
            $f.prop('required', true).attr('required', true);
            $f.removeClass('required-if-showed');
        });
    }

    // Remove required from fields inside the container and mark them for future restore
    removeRequiredInside($container) {
        $container.find('input, select, textarea').each(function () {
            const $f = $(this);
            if ($f.is('[required]')) {
                // Mark as previously required & remove the attribute
                $f.addClass('required-if-showed');
                $f.prop('required', false).removeAttr('required');
            }
        });
    }

    checkTriggers($container, triggers) {
        let shouldShow = true;

        // Evaluate triggers
        for (let trigger in triggers) {
            const expectedValue = String(triggers[trigger]);
            const $inputTrigger = $('input[name=' + trigger + ']');
            let inputValue = '';

            if ($inputTrigger.attr('type') === 'checkbox') {
                inputValue = $inputTrigger.is(':checked') ? 'true' : 'false';
            } else if ($inputTrigger.attr('type') === 'radio') {
                inputValue = $('input[name=' + trigger + ']:checked').val() || '';
            }

            if (String(inputValue) !== expectedValue) {
                shouldShow = false;
                break;
            }
        }

        if (shouldShow) {
            // Show and restore required only for fields that had it before
            $container.addClass('-show');
            this.restoreRequiredInside($container);
        } else {
            // Hide: remove required inside and remember which fields had it
            $container.removeClass('-show');
            this.removeRequiredInside($container);
            this.clearContainerTextInputs($container);
        }
    }

    uncheckTriggers($container, triggers) {
        let shouldHide = true;

        // Evaluate triggers: if all match -> hide
        for (let trigger in triggers) {
            const expectedValue = String(triggers[trigger]);
            const $inputTrigger = $('input[name=' + trigger + ']');
            let inputValue = '';

            if ($inputTrigger.attr('type') === 'checkbox') {
                inputValue = $inputTrigger.is(':checked') ? 'true' : 'false';
            } else if ($inputTrigger.attr('type') === 'radio') {
                inputValue = $('input[name=' + trigger + ']:checked').val() || '';
            }

            if (String(inputValue) !== expectedValue) {
                shouldHide = false;
                break;
            }
        }

        if (shouldHide) {
            // Add -hide, strip required inside (mark which ones to restore later)
            $container.addClass('-hide');
            this.removeRequiredInside($container);
            this.clearContainerTextInputs($container);
        } else {
            // Remove -hide and restore required only for previously-required fields
            $container.removeClass('-hide');
            this.restoreRequiredInside($container);
        }
    }

    clearContainerTextInputs($container) {
        const $inputs = $container.find('input, select')
            .not('[type="radio"], [type="checkbox"], [type="button"], [type="submit"]');

        $inputs.each(function () {
            const $f = $(this);

            const $wrap = $f.parent();
            $wrap.removeClass('error');
            $wrap.find('label.error').remove();
        });
    }

    validateForm() {
        this.setupFormValidation();
        this.showHiddenFields();
        this.hideShownFields();
    }

    validateInputs() {
        const form = $(SELECTORS.FORM);
        const inputs = form.find(':input');
        const selects = form.find('select');
        let allValid = true;

        inputs.each(function () {
            let $containerOfInput = $(this).parent();

            if(!$containerOfInput.hasClass('required-if-showed')) {
                form.validate().element(this);
                if (!this.checkValidity() || $containerOfInput.hasClass('error')) {
                    allValid = false;
                }
            }
        });

        selects.each(function () {
            let $containerOfInput = $(this).parent();

            if(!$containerOfInput.hasClass('required-if-showed')) {
                form.validate().element(this);
                if (!this.checkValidity() || $containerOfInput.hasClass('error')) {
                    allValid = false;
                }
            }
        });

        return allValid;
    }

    scrollToElem(elem) {
        $("html, body").animate({
            scrollTop: elem.offset().top
        }, 600);
    }

    async initStripe() {
        if (!window.BL_STRIPE || !BL_STRIPE.pk) return;

        this.stripe = Stripe(BL_STRIPE.pk);
        this.elements = this.stripe.elements({
            locale: 'auto',
            fonts: [
                { cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap' }
            ]
        });

        const elementStyle = {
            base: {
                color: '#020817',                 // text-gray-800
                fontSize: '14px',
                lineHeight: '20px',
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                '::placeholder': { color: '#64748b' }
            },
            invalid: { color: '#020817'}
        };

        this.cardNumber = this.elements.create('cardNumber', { style: elementStyle });
        this.cardExpiry = this.elements.create('cardExpiry', { style: elementStyle });
        this.cardCvc    = this.elements.create('cardCvc',    { style: elementStyle });

        this.cardNumber.mount('#bl-card-number');
        this.cardExpiry.mount('#bl-card-expiry');
        this.cardCvc.mount('#bl-card-cvc');

        const ensureWrap = (selector) => {
            const $el = $(selector);
            let $wrap = $el.closest('.bl-stripe-field');
            if (!$wrap.length) { // підстрахуємось, якщо клас не додали у верстці
                $wrap = $el.parent().addClass('bl-stripe-field');
            }
            return $wrap;
        };
        const $numWrap = ensureWrap('#bl-card-number');
        const $expWrap = ensureWrap('#bl-card-expiry');
        const $cvcWrap = ensureWrap('#bl-card-cvc');

        const bindWrap = (el, $w) => {
            el.on('focus', () => $w.addClass('is-focus'));
            el.on('blur',  () => $w.removeClass('is-focus'));
            el.on('change', (ev) => $w.toggleClass('is-error', !!ev.error));
        };
        bindWrap(this.cardNumber, $numWrap);
        bindWrap(this.cardExpiry, $expWrap);
        bindWrap(this.cardCvc,    $cvcWrap);

        const attachValidityMirror = (el, sel) => {
            const $host = $(sel);
            el.on('change', (ev) => {
                const $iframe = $host.find('.StripeElement');
                const isOk = !!ev.complete && !ev.error;
                $iframe.toggleClass('validation-error', !isOk);
                $host.closest('.bl-stripe-field').toggleClass('is-error', !isOk);
            });
        };
        attachValidityMirror(this.cardNumber, '#bl-card-number');
        attachValidityMirror(this.cardExpiry, '#bl-card-expiry');
        attachValidityMirror(this.cardCvc,    '#bl-card-cvc');

        const $err = $('#bl-card-errors');
        const showErr = (ev) => { $err.text(ev.error ? ev.error.message : ''); };
        this.cardNumber.on('change', showErr);
        this.cardExpiry.on('change', showErr);
        this.cardCvc.on('change', showErr);

        // this.$form.off('submit.bl').on('submit.bl', (e) => {
        //     e.preventDefault();
        //     this.onSubmitStripeFlow();
        // });
    }

    // --- Stripe validation: all split elements must be complete
    validateStripeElements(scroll = true) {
        if (this.getPaymentMethod() === 'wire-transfer') return true;
        if (!this.submitAttempted) return true;

        const hosts = ['#bl-card-number', '#bl-card-expiry', '#bl-card-cvc'];

        // читаємо статус: complete на ХОСТІ або (про всяк випадок) на дочірньому елементі
        const isHostComplete = (sel) => {
            const $host = $(sel);
            return $host.hasClass('StripeElement--complete') ||
                $host.find('.StripeElement').hasClass('StripeElement--complete');
        };

        const completeArr = hosts.map(isHostComplete);
        const allComplete = completeArr.every(Boolean);

        // синхронізуємо UI-класи (прибираємо застарілі is-error)
        hosts.forEach((sel, i) => {
            const $host = $(sel);
            const $wrap = $host.closest('.bl-stripe-field').length
                ? $host.closest('.bl-stripe-field')
                : $host.closest('label');
            const ok = !!completeArr[i];

            // ставимо/знімаємо тільки на хості та обгортці
            $host.toggleClass('validation-error', !ok);
            $wrap.toggleClass('is-error', !ok);
        });

        if (allComplete) return true;

        // фокус/скрол до першого неповного
        const badIdx = completeArr.findIndex(v => !v);
        if (badIdx !== -1 && scroll) {
            const badSel = hosts[badIdx];
            const $target = $(badSel).closest('.bl-stripe-field').length
                ? $(badSel).closest('.bl-stripe-field')
                : $(badSel).closest('label');

            this.scrollToElem($target);
            setTimeout(() => {
                if (badSel === '#bl-card-number') this.cardNumber?.focus && this.cardNumber.focus();
                else if (badSel === '#bl-card-expiry') this.cardExpiry?.focus && this.cardExpiry.focus();
                else if (badSel === '#bl-card-cvc') this.cardCvc?.focus && this.cardCvc.focus();
            }, 0);
        }
        return false;
    }

    async onSubmitStripeFlow() {
        const $btn = this.$form.find('button[type="submit"]');
        $btn.prop('disabled', true).addClass('loading');
        try {
            const clientSecret  = await this.createPaymentIntent();
            const paymentIntent = await this.confirmCardPayment(clientSecret);
            await this.finalizeBooking(paymentIntent);
        } catch (err) {
            this.showStripeError(this.parseAjaxError(err));
            throw err; // нехай підніметься у submitHandler для загальної логіки
        } finally {
            $btn.prop('disabled', false).removeClass('loading');
        }
    }

    async createPaymentIntent() {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';
        const amount  = Math.round((this.lastTotal || 0) * 100); // CHF → rappen

        const extras = Array.from(this.selectedExtras.values()).map(x => ({
            id: x.id, name: x.name, price: Number(x.price)||0, frequency: x.frequency||''
        }));

        let res;
        try {
            res = await $.ajax({
                url: ajaxUrl, method: 'POST', dataType: 'json',
                data: {
                    action: 'bl_stripe_create_intent', _ajax_nonce: nonce,
                    amount, currency: this.currency,
                    apartmentId: this.apartmentId,
                    dateFrom: this.dateFrom, dateTo: this.dateTo,
                    guests: this.guests,
                    extras: JSON.stringify(extras),
                    token: this.checkoutToken
                }
            });
        } catch (e) {
            const msg = this.parseAjaxError(e);
            $('#bl-card-errors').text(msg);
            this.showFormError(msg);
            throw new Error(msg);
        }

        if (!res?.success || !res?.data?.clientSecret) {
            throw new Error(res?.data?.message || t('payment_intent_failed', 'Failed to create payment intent.'));
        }
        return res.data.clientSecret;
    }

    async confirmCardPayment(clientSecret) {
        const billingDetails = this.getStripeBillingDetails();

        const { paymentIntent, error } = await this.stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: this.cardNumber, billing_details: billingDetails }
        });

        console.log('[BL][Stripe confirm]', {
            error: error ? { message: error.message, type: error.type, code: error.code } : null,
            paymentIntent: paymentIntent ? {
                id: paymentIntent.id,
                status: paymentIntent.status,
                amount: paymentIntent.amount,
                currency: paymentIntent.currency,
                created: paymentIntent.created
            } : null
        });

        if (error) {
            $('#bl-card-errors').text(error.message || t('card_confirmation_failed', 'Card confirmation failed.'));
            throw error;
        }
        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
            const msg = t('payment_not_completed', 'Payment not completed.');
            throw new Error(msg);
        }
        return paymentIntent;
    }

    async finalizeBooking(paymentIntent, paymentMethod = null) {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        const pm = paymentMethod || this.getPaymentMethod();

        const $country = this.$form.find('select[name="country"] option:selected');
        const countryCode = String($country.val()||'').trim();
        const countryName = String($country.text()||'').trim();

        const customer = {
            firstName: String(this.$form.find('input[name="firstName"]').val() || '').trim(),
            lastName:  String(this.$form.find('input[name="lastName"]').val()  || '').trim(),
            email:     String(this.$form.find('input[name="email"]').val()      || '').trim(),
            phone:     String(this.$form.find('select[name="phone-code"]').val() || '').trim() + String(this.$form.find('input[name="phoneNumber"]').val() || '').trim(),
            address1:  String(this.$form.find('input[name="address"]').val()    || '').trim(),
            city:      String(this.$form.find('input[name="city"]').val()       || '').trim(),
            zip:       String(this.$form.find('input[name="zipCode"]').val()        || '').trim(),
            country:   String(countryName   || '').trim(),
        };

        const useSameBilling = $('#sameBilling').is(':checked');
        let billingCustomer = null;

        if (!useSameBilling) {
            const $bcountry = this.$form.find('select[name="additional-country"] option:selected');
            const billingCountryName = String($bcountry.text()||'').trim();

            billingCustomer = {
                firstName: String(this.$form.find('input[name="billingFirstName"]').val() || '').trim(),
                lastName:  String(this.$form.find('input[name="billingLastName"]').val()  || '').trim(),
                email:     String(this.$form.find('input[name="billingEmail"]').val()      || '').trim(),
                phone:     String(this.$form.find('select[name="additional-phone-code"]').val() || '').trim() +
                    String(this.$form.find('input[name="billingPhoneNumber"]').val() || '').trim(),
                address1:  String(this.$form.find('input[name="billingAddress"]').val()    || '').trim(),
                city:      String(this.$form.find('input[name="billingCity"]').val()       || '').trim(),
                zip:       String(this.$form.find('input[name="billingZipCode"]').val()    || '').trim(),
                country:   billingCountryName
            };
        }

        const extras = Array.from(this.selectedExtras.values());
        const rateId = this.getSelectedRateId();

        const guestsArr = [];
        this.$form.find('fieldset').each(function(){
            const $fs = $(this);
            const f = {
                firstName: $fs.find('input[name*="[firstName]"]').val()?.trim() || '',
                lastName:  $fs.find('input[name*="[lastName]"]').val()?.trim() || '',
                email:     $fs.find('input[name*="[email]"]').val()?.trim() || '',
                phoneCode: $fs.find('select[name*="[phoneCode]"]').val() || '',
                phoneNumber: $fs.find('input[name*="[phoneNumber]"]').val()?.trim() || ''
            };
            if (f.firstName && f.lastName && f.email) {
                f.phone = [f.phoneCode, f.phoneNumber].filter(Boolean).join(' ');
                guestsArr.push({ firstName: f.firstName, lastName: f.lastName, email: f.email, phone: f.phone });
            }
        });

        const payload = {
            action: 'bl_finalize_booking',
            _ajax_nonce: nonce,
            amount: Math.round((this.lastTotal || 0) * 100),
            currency: this.currency,
            apartmentId: this.apartmentId,
            unitName: this.apartmentInfo.unitName,
            unitLocation: this.apartmentInfo.addressLine1 + ', ' + this.apartmentInfo.areaName + ', ' + this.apartmentInfo.city,
            dateFrom: this.dateFrom, dateTo: this.dateTo,
            guests: this.guests,
            rateId,
            extras: JSON.stringify(extras),
            customer: JSON.stringify(customer),
            additionalGuests: JSON.stringify(guestsArr),
            billingCustomer: billingCustomer ? JSON.stringify(billingCustomer) : '',
            payment_method: pm,
            token: this.checkoutToken,
            lang: (window.BL_AJAX && BL_AJAX.lang) || ''
        };

        if (pm !== 'wire-transfer' && paymentIntent) {
            payload.payment_intent_id = paymentIntent.id;
        }
        let res;
        try {
            const res = await $.ajax({ url: ajaxUrl, method: 'POST', dataType: 'json', data: payload });
            if (!res?.success) throw res;
            const redirect = res?.data?.redirect;
            if (redirect) window.location.href = `${redirect}`;
            else alert('redirect failed');
        } catch (err) {
            this.showGlobalError(t('something_went_wrong', 'Something went wrong.'));
            throw err;
        }
    }


    /* ---------- BOOT (Promise chain) ---------- */

    async boot() {
        // sanity
        if (!this.apartmentId || !this.dateFrom || !this.dateTo) {
            this.showError(t('booking_token_is_invalid', 'Booking token is invalid or missing required data.'));
            return;
        }

        this.showLoader();

        // 1) availability
        try {
            await this.checkAvailability();
        } catch (e) {
            this.showError(e?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.'));
            return;
        }

        try {
            await this.loadFormHtml();
            await this.loadPhonesAjax();
            this.populateDialCodesFromData();
            this.populateCountriesFromData();
        } catch (e) {
            this.showError(e?.message || t('could_not_load_the_booking_form', 'Could not load the booking form.'));
            return;
        }

        await this.initStripe();

        // 2) load apartment info (extras + nice card)
        try {
            await this.fetchApartmentInformation();
        } catch {
            // not critical: we can proceed without additional fields
        }

        this.hydrateSelectedExtrasFromPayload();

        this.pruneHiddenSelectedExtras?.();

        // 3) render UI pieces
        this.renderApartmentCard();
        this.renderExtras();
        const total = this.renderPriceBox();

        // 4) write total into the submit button
        this.setPayButtonAmount(total);

        // 5) show the page
        this.showContent();

        // bind extra clicks if we need to recalculate later
        this.bindExtraClicks();
    }

    bindExtraClicks() {
        this.$extras.off('click.js-extra')
            .on('click.js-extra', '.js-extra-book', (e) => {
                const $btn = $(e.currentTarget);
                const id   = String($btn.data('id'));
                const extra = {
                    id,
                    name: String($btn.data('name') || ''),
                    price: Number($btn.data('price') || 0),
                    frequency: String($btn.data('frequency') || '')
                };

                if (this.selectedExtras.has(id)) {
                    this.selectedExtras.delete(id);          // remove selection
                } else {
                    this.selectedExtras.set(id, extra);      // select (price 0 if missing)
                }

                this.renderExtras();                       // update cards (green state)
                const total = this.renderPriceBox();       // update price box with list of extras
                this.setPayButtonAmount(total);            // update amount on the button
            });
    }
}

/* ------------------------ INIT ------------------------ */
const shortTermBookingApi = new ShortTermBookingApi();
$(document).ready(() => shortTermBookingApi.boot());

$(document).on('change', 'input[type=checkbox], input[type=radio]', function () {
    $('[data-show-triggers]').each(function () {
        const $container = $(this);
        const triggers = JSON.parse($container.attr('data-show-triggers').replace(/'/g, '"'));
        shortTermBookingApi.checkTriggers($container, triggers);
    });
    $('[data-hide-triggers]').each(function () {
        const $container = $(this);
        const triggers = JSON.parse($container.attr('data-hide-triggers').replace(/'/g, '"'));
        shortTermBookingApi.uncheckTriggers($container, triggers);
    });
});